import {IPromise} from "angular";
import {l} from "../../../../../types/typescriptdefinisjoner_lederplan";
import {tilKolisjonsfrieÅrshjulinnslagsamlinger} from "../årshjul/årshjul";
import {dateSomPlainDate, dateSomZonedDateTime, plainDateSomDate, zonedDateTimeSomDate} from "felles-ng2/støttefunksjoner/temporalTilDateKonverteringer";
import {erTomListe, første, reduserTilFørsteInnslag, reduserTilFørsteInnslagEller, reduserTilMaks, reduserTilMin, reduserTilSum, siste} from "felles-ng2/støttefunksjoner/lister";
import {GruppeId, MøteId} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {erSammeÅrOgMånedPlainDate, erSammeÅrOgMånedZonedDateTime, erZonedDateTimeSenereEnn, zonedDateTimeFørsteDagIMåneden, zonedDateTimeSisteDagIMåneden} from "felles-ng2/støttefunksjoner/temporal";
import {mapSomObjekt, mapVerdierIMap} from "felles-ng2/støttefunksjoner/map";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Temporal} from "temporal-polyfill";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {GjeldendeTidssone, Tidssoneinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import PlainDate = Temporal.PlainDate;
import ZoneDateTime = Temporal.ZonedDateTime;
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";

const debug = initDebugger('MøterIPlanModell');

/** Beregner posisjoner (i piksler) for møter og årshjulinnslag i årsplanvisningen.
 *  Hver gruppe vises på egen rad, og hver måned blir så bred som møtene i måneden krever.
 *  Posisjonene beregnes først med faste bredder, og skaleres deretter til tilgjengelig bredde.
 *  Ved mange møter kan starten og/eller slutten av planen trunkeres. */
export class MøterIPlanModell {

    //konstante verdier som bestemmer bredder på labels- og møter-kontainer
    /** luft på sidene av planen */
    private readonly margin = 40;
    /** bredde på element som vises når planen er trunkert pga for mange møter */
    private readonly trunkertKontainerBredde = 55;
    /** hvor stor andel av tilgjengelig plass labels (gruppenavn) skal ta */
    private readonly relativLabelKontainerBredde = 0.2;
    /** minimum bredde på labels-kontainer */
    private readonly minLabelsKontainerBredde = 150;
    /** minimum bredde på møter-kontainer (.wdkp-rad-innhold-kontainer .wdkp-gruppe-kontainer i css)*/
    private readonly minMøterKontainerBredde = 600;

    private readonly minMndBredde = 50;
    private readonly møteBredde = 60;
    private readonly møteHorisontalMargin = 5;

    moeterPerMaaned: MånedInfo[] = [];
    trunkeringsinfo: Trunkeringsinfo = Trunkeringsinfo.ingenTrunkering();
    /** @deprecated bruk aarshjulSomMap */
    aarshjul: { [gruppeId: string]: Årshjulinfo } = {};
    aarshjulSomMap: Map<GruppeId, Årshjulinfo> = new Map();
    labelsKontainerBredde = this.minLabelsKontainerBredde;
    moeterKontainerBredde = this.minMøterKontainerBredde;
    /** pos til "nå" i tidslinjen, som pixel posisjon left. Settes av {@link #beregnNåPosisjon()} */
    naaPos!: number;

    /** Perioden vi viser møter for */
    private periode!: l.PlanPeriode; //settes i initModell()
    /** Møter i angitt periode, sortert etter dato stigende */
    private møter!: l.MøteModellEnkel[]; //settes i initModell()
    private grupper!: l.EnkelGruppeModell[]; //settes i initModell()
    private møterEtterId!: Map<MøteId, MøteInfo>; //settes i initModell()
    //public for testbarhet
    posisjonUker!: Map<number, TlTrPosisjon>; //settes i initModell()
    private ikkeTrunkerStart = false;
    private ikkeTrunkerSlutt = false;

    constructor(private readonly klokke: Klokke,
                private readonly gjeldendeTidssone: GjeldendeTidssone,
                private readonly årshjulinnslagLaster: () => IPromise<ÅrshjulinnslagPerGruppe>,
                private readonly kontainerbredde: () => number) {
    }

    initModell(periode: l.PlanPeriode, møter: l.MøteModellEnkel[], grupper: l.EnkelGruppeModell[]): IPromise<void> {
        this.periode = periode;
        //møter må være sortert for stabilt resultat
        this.møter = møter.sort((a, b) => a.dato.start.getTime() - b.dato.start.getTime());
        this.grupper = grupper;

        return this.beregnPosisjoner();
    }

    initÅrshjul() {
        debug("initÅrshjul", this.posisjonUker, this.moeterPerMaaned)
        return this.årshjulinnslagLaster().then(innslagPerGruppe => {
            this.aarshjulSomMap = Object.entries(innslagPerGruppe).reduce((årshjulPerGruppe, [gruppeId, årshjul]) =>
              årshjulPerGruppe.set(gruppeId, this.beregnÅrshjulinnslagForGruppe(årshjul)), new Map<GruppeId, Årshjulinfo>())

            //kan droppes når this.aarshjul endres til Map
            this.aarshjul = mapSomObjekt(this.aarshjulSomMap);
        });
    }

    opphevTrunkeringStart(): IPromise<void> {
        this.ikkeTrunkerStart = true;
        //beregnPosisjoner() vil nå inkludere alle møter i starten siden ikkeTrunkerStart==true
        return this.beregnPosisjoner();
    }

    opphevTrunkeringSlutt(): IPromise<void> {
        this.ikkeTrunkerSlutt = true;
        //beregnPosisjoner() vil nå inkludere alle møter i slutten siden ikkeTrunkerSlutt==true
        return this.beregnPosisjoner();
    }

    møteInfoForMøte(møteId: string): MøteInfo {
        return this.møterEtterId.get(møteId)!;
    }

    //hjelpemetoder

    //møterelatert

    /** Beregner absolutt posisjon av møter og årshjulinnslag,
     * og skalerer posisjonene iht tilgjengelig vindusbredde. */
    private beregnPosisjoner(): IPromise<void> {
        this.moeterPerMaaned = this.beregnMøterPerMaaned(); //må være kalt før beregnPosisjonUker() kalles
        this.møterEtterId = this.moeterPerMaaned
          .flatMap(mndInfo => mndInfo.moeterIMnd)
          .reduce((møter, møte) => møter.set(møte.moete.id, møte), new Map<MøteId, MøteInfo>());
        this.posisjonUker = this.beregnPosisjonUker(); //skalerPlanIhtVindusbredde() forventer at posisjonUker er initialisert
        return this.initÅrshjul()
          .then(() => this.skalerPlanIhtVindusbredde());
    }

    /** Beregner plassering av møter per måned per gruppe.
     * Merk at denne beregning ikke tar hensyn til tilgjengelig bredde (vinduets bredde).
     * Bredder blir beregnet utfra konstantene som bla {@link #møteBredde} */
    private beregnMøterPerMaaned(): MånedInfo[] {
        var start = this.somPlainDate(this.periode.start);
        var antallMåneder = start.until(this.somPlainDate(this.periode.slutt)).total({unit: "months", relativeTo: start});
        const månedInfoer = new Array(antallMåneder)
          .fill(null) //uten dette kan vi ikke iterere over arrayen
          .map((_, indeks) => start.add({months: indeks}))
          .map(måned => this.opprettMånedInfo(this.møterIMåned(måned), måned))

        //for at planen ikke skal bli uleselig trunkerer vi start og slutt ved mange møter (fjerner måneder fra møterPerMåned)
        const [trunkeringsinfo, månedInfoerTrunkert] = this.trunkerMånederTilMaksAntallMøterNådd(månedInfoer);
        this.trunkeringsinfo = trunkeringsinfo;

        //oppdater månedInfo med beregnet start og slutt pos, må gjøres etter at trunkerMånederTilMaksAntallMøterNådd() har blitt kalt
        månedInfoerTrunkert.forEach((månedInfo, index) => this.oppdaterPosisjoner(månedInfo, index, månedInfoerTrunkert));

        debug('beregnMøterPerMaaned', månedInfoerTrunkert);
        return månedInfoerTrunkert;
    }

    private møterIMåned(måned: PlainDate): l.MøteModellEnkel[] {
        return this.møter.filter(møte => erSammeÅrOgMånedPlainDate(måned, this.somPlainDate(møte.dato.start)));
    }

    /** Beregner {@link MånedInfo} for angitte møter og måned.
     * Merk at verdier for pos (tl/tr/bredde) settes senere via {@link #oppdaterPosisjoner} og {@link #oppdaterSkalertePosisjonerMøter} */
    private opprettMånedInfo(møter: l.MøteModellEnkel[], måned: PlainDate): MånedInfo {
        const møteInfoer: MøteInfo[] = møter.map(møte => ({
            moete: møte,
            pos: {tl: 0, tr: 0, bredde: this.møteBredde},
            skalertPos: {tl: 0, tr: 0, bredde: this.møteBredde},
        }));
        const møterPerGruppe = this.grupper.reduce((gruppeIdOgMøter, gruppe) =>
          gruppeIdOgMøter.set(gruppe.id, møteInfoer.filter(møteinfo => møteinfo.moete.eiendeGruppeId == gruppe.id)), new Map<GruppeId, MøteInfo[]>());
        const maxAntallMøterPerGruppe = [...møterPerGruppe.values()].map(møteInfos => møteInfos.length).reduce(...reduserTilMaks());

        return {
            pos: {tl: 0, tr: 0, bredde: 0},
            skalertPos: {tl: 0, tr: 0, bredde: 0},
            skalering: 1,
            mnd: plainDateSomDate(måned),
            mndTemporal: måned,
            moeterIMnd: møteInfoer,
            moeterPerGruppe: mapSomObjekt(møterPerGruppe),
            moeterPerGruppeSomMap: møterPerGruppe,
            maxAntallMøterPerGruppe: maxAntallMøterPerGruppe
        }
    }

    private oppdaterPosisjoner(mndInfo: MånedInfo, index: number, mndInfos: MånedInfo[]) {
        //Sporer sist plasserte møte per gruppe i måneden - brukes slik at et møte ikke plasseres oppå forrige møte i samme gruppe,
        //også når møter i andre grupper ligger mellom dem i lista
        const forrigeMøtePerGruppe = new Map<l.MøteModellEnkel['eiendeGruppeId'], MøteInfo>();

        mndInfo.moeterIMnd.forEach((møteInfo: MøteInfo, index: number) => {
            møteInfo.pos.tl = this.kalkulerMøteTopLeft(møteInfo, index, mndInfo.moeterIMnd, forrigeMøtePerGruppe.get(møteInfo.moete.eiendeGruppeId));
            møteInfo.pos.tr = møteInfo.pos.tl + møteInfo.pos.bredde;
            møteInfo.skalertPos = {...møteInfo.pos}; //justeres av oppdaterSkalertePosisjonerMøter()
            forrigeMøtePerGruppe.set(møteInfo.moete.eiendeGruppeId, møteInfo);
        });

        mndInfo.pos.bredde = erTomListe(mndInfo.moeterIMnd) ? this.minMndBredde : siste(mndInfo.moeterIMnd)!.pos.tr + this.møteHorisontalMargin;
        mndInfo.pos.tl = index == 0 ? 0 : (mndInfos[index - 1].pos.tr);
        mndInfo.pos.tr = mndInfo.pos.tl + mndInfo.pos.bredde;
        //gjør møteInfo pos absolutt
        mndInfo.moeterIMnd.forEach(møteInfo => {
            møteInfo.pos.tl = mndInfo.pos.tl + møteInfo.pos.tl;
            møteInfo.pos.tr = mndInfo.pos.tl + møteInfo.pos.tr;
            møteInfo.skalertPos = {...møteInfo.pos}; //justeres av oppdaterSkalertePosisjonerMøter()
        });
        mndInfo.skalertPos = {...mndInfo.pos}; //justeres av oppdaterSkalertePosisjonerMøter()
        return mndInfo;
    }

    /** Beregner top-left plassering av møteboks, relativt til månedens start. Forutsetter at møteInfos er sortert på møtedato.
     *  Møtet plasseres aldri oppå (helt eller delvis) forrige møte i samme gruppe, illustrert (møteInfo = A2, forrige: B1, forrigeSammeGruppe = A1, alle på samme dag):
     *  <pre>
     *  *A1* *A2*  | og ikke som A2 oppå A1: *A2*
     *  *B1*       |                         *B1*
     *  </pre> */
    private kalkulerMøteTopLeft(møteInfo: MøteInfo, index: number, møteInfos: MøteInfo[], forrigeMøteSammeGruppe: MøteInfo | undefined) {
        const førsteLedigeTopLeft = erIkkeNil(forrigeMøteSammeGruppe) ? forrigeMøteSammeGruppe.pos.tr + this.møteHorisontalMargin : this.møteHorisontalMargin;
        if (index == 0) return førsteLedigeTopLeft;//første møte i måneden
        const forrigeMøte = møteInfos[index - 1];

        //forrige møte er i samme gruppe - plasserer møte på samme linje som forrige, illustrert (møteInfo = A2):
        // *A1* *A2* *A3*
        if (forrigeMøte.moete.eiendeGruppeId == møteInfo.moete.eiendeGruppeId)
            return forrigeMøte.pos.tr + this.møteHorisontalMargin * 2;

        //håndter plassering av møter i ulike grupper
        const topLeftIForholdTilMøteIAnnenGruppe = this.kalkulerMøteTopLeftIfhtMøteIAnnenGruppe(møteInfo, index, møteInfos, forrigeMøte, forrigeMøteSammeGruppe);
        return Math.max(førsteLedigeTopLeft, topLeftIForholdTilMøteIAnnenGruppe);
    }

    /** Beregner top-left plassering av møteboks ut fra forrige møte, når forrige møte er i en annen gruppe.
     *  Samme dag som forrige møte gir plassering rett under, ellers forskyves møtet en halv eller hel møtebredde.
     *  Tar ikke hensyn til overlapp med forrige møte i samme gruppe, det håndteres av {@link #kalkulerMøteTopLeft}. */
    private kalkulerMøteTopLeftIfhtMøteIAnnenGruppe(møteInfo: MøteInfo, index: number, møteInfos: MøteInfo[], forrigeMøte: MøteInfo, forrigeMøteSammeGruppe: MøteInfo | undefined) {
        //dersom samme dag, vis direkte under, illustrert (møteInfo = B2):
        // *A1* *A2* *A3*
        //      *B2*
        if (this.somPlainDate(forrigeMøte.moete.dato.start).equals(this.somPlainDate(møteInfo.moete.dato.start)))
            return forrigeMøte.pos.tl;

        //dersom ikke samme dag, vis forskjøvet 'en halv møtebredde', illustrert (møteInfo = B2):
        // *A1*
        //   *B2*
        let forskyvning = 0.5;
        const nesteMøte = index < møteInfos.length - 1 ? møteInfos[index + 1] : null;
        //dersom neste møte er i annen gruppe men på samme dag, forskyv 'en hel møtebredde', illustrert (møteInfo = B2, neste = A2):
        // *A1* *A2*  | og ikke som halv-forskjøvet som ville blitt: *A1A2*
        //      *B2*  |                                                *B2*
        if (erIkkeNil(nesteMøte) &&
          nesteMøte.moete.eiendeGruppeId != møteInfo.moete.eiendeGruppeId &&
          this.somPlainDate(nesteMøte.moete.dato.start).equals(this.somPlainDate(møteInfo.moete.dato.start)))
            forskyvning = 1;
        //dersom forrige møte i samme gruppe er på samme dag som forrige møte, forskyv 'en hel møtebredde', illustrert (møteInfo = A2, forrige: B1, forrigeSammeGruppe = A1):
        // *A1* *A2*  | og ikke som halv-forskjøvet som ville blitt: *A1A2*
        // *B1*       |                                              *B1*
        if (erIkkeNil(forrigeMøteSammeGruppe) &&
          this.somPlainDate(forrigeMøteSammeGruppe.moete.dato.start).equals(this.somPlainDate(forrigeMøte.moete.dato.start)))
            forskyvning = 1;

        return forrigeMøte.pos.tl + (this.møteBredde * forskyvning) + this.møteHorisontalMargin;
    }

    /** Beregner breddene for alle 52 uker i et år.
     *  Beregnes basert på bredde per måned satt i {@link #moeterPerMaaned}.
     *  Returnerte posisjoner tar ikke høyde for tilgjengelig vindusbredde.
     *  Tilpasning til vindusbredde håndteres via {@link #skalerPlanIhtVindusbredde}.
     *
     *  Merk at vi i visning av årshjulinnslag kun opererer med 52 uker, selvom enkelte år kan inneholde 53 uker.
     *
     *  Fra Temporal dokumentasjonen for PlainDate.weekOfYear:
     *  For the ISO 8601 calendar, this is normally a value between 1 and 52, but in a few cases it can be 53 as well.
     *  ISO week 1 is the week containing the first Thursday of the year. */
    private beregnPosisjonUker(): Map<number, TlTrPosisjon> {
        let posisjonUker = new Map<number, TlTrPosisjon>();
        let dagPos = 0, forrigeUkeSluttPos = 0;
        let dag = this.somPlainDate(this.moeterPerMaaned[0].mnd);
        let ukeNr = dag.weekOfYear!;

        //bredde per uke må beregnes utfra bredde per måned
        this.moeterPerMaaned.forEach(månedInfo => {
            const førsteDagIMnd = this.somPlainDate(månedInfo.mnd)!;
            debug(`registrerer uker for måned ${dag.toString()}`)
            const antallDagerIMnd = førsteDagIMnd.daysInMonth;
            const pikslerPerDag = månedInfo.pos.bredde / antallDagerIMnd;

            //for hver dag i måneden
            while (dag.month == førsteDagIMnd.month) {
                if (dag.weekOfYear != ukeNr) {
                    //vi har kommet inn i ny uke, registrer forrige uke
                    posisjonUker.set(ukeNr, {tl: forrigeUkeSluttPos, tr: dagPos, bredde: dagPos - forrigeUkeSluttPos});
                    debug(`registrerer uke ${ukeNr}, dag: ${dag.toString()}`, posisjonUker.get(ukeNr))
                    forrigeUkeSluttPos = dagPos;
                    ukeNr = dag.weekOfYear!;
                }
                dag = dag.add({days: 1});
                dagPos += pikslerPerDag;
            }
        });

        //juster for at året ikke har nøyaktig 52 hele uker
        const skalering = siste(this.moeterPerMaaned)!.skalertPos.tr / [...posisjonUker.values()].map(ukePos => ukePos.tr).reduce(...reduserTilMaks());
        posisjonUker = mapVerdierIMap(posisjonUker, posisjon => this.skalerPosisjon(posisjon, skalering));

        debug('posisjonUker', posisjonUker)
        return posisjonUker;
    }

    private skalerPosisjon(pos: TlTrPosisjon, skalering: number): TlTrPosisjon {
        debug('skalerPosisjon', pos.bredde, Math.floor(pos.bredde * skalering))
        return {bredde: Math.floor(pos.bredde * skalering), tl: Math.floor(pos.tl * skalering), tr: Math.floor(pos.tr * skalering)};
    }

    /** Fjerner møter fra start og slutt til antall møter ikke overskrider det vi mener det er plass til på skjermen.
     * Vi trunkerer kun dersom vist plan inneholder dagens dato.
     * Returnerer trunkeringsinfo og en potensielt trunkert versjon av angitte møterPerMåned.*/
    private trunkerMånederTilMaksAntallMøterNådd(møterPerMåned: MånedInfo[]): [Trunkeringsinfo, MånedInfo[]] {
        const møterPerMånedTrunkert = [...møterPerMåned] //vi fjerner måneder fra denne samlingen til vi får plass til måneden på skjermen
        //kun trunker dersom antall møter overstiger det vi mener at vi har plass til gitt skjermbredden
        const maksAntallMøter = Math.floor(this.tilgjengeligeHorisontalPlass().møterBredde / (this.møteBredde + this.møteHorisontalMargin * 2));
        const sumMaxAntallMøterPerGruppe = møterPerMånedTrunkert.map(månedInfo => månedInfo.maxAntallMøterPerGruppe).reduce(...reduserTilSum());
        if (sumMaxAntallMøterPerGruppe <= maksAntallMøter) return [Trunkeringsinfo.ingenTrunkering(), møterPerMånedTrunkert];

        //bruker har valgt å se alle møter - både i start og slutt
        if (this.ikkeTrunkerStart && this.ikkeTrunkerSlutt) return [Trunkeringsinfo.ingenTrunkering(), møterPerMånedTrunkert];

        //for mange møter - la oss fjerne noen..
        debug(`trunkerMånederTilMaksAntallMøterNådd, maksAntallMøter ${maksAntallMøter} er lavere enn sumMaxAntallMøterPerGruppe ${sumMaxAntallMøterPerGruppe}, fjerner måneder for å få plass`, møterPerMånedTrunkert)
        let månederFjernetFraStart: MånedInfo[] = [], månederFjernetFraSlutt: MånedInfo[] = [];
        const nå = new Date();

        let antallMøterFør = møterPerMånedTrunkert.filter(måned => måned.mnd.getTime() < nå.getTime()).map(måned => måned.maxAntallMøterPerGruppe).reduce(...reduserTilSum());
        let antallMøterEtter = møterPerMånedTrunkert.filter(måned => måned.mnd.getTime() > nå.getTime()).map(måned => måned.maxAntallMøterPerGruppe).reduce(...reduserTilSum());

        //fjern måneder omkring nåMåned til vi er under maksAntallMøter - fjern fra den siden som har flest møter
        while (møterPerMånedTrunkert.map(månedInfo => månedInfo.maxAntallMøterPerGruppe).reduce(...reduserTilSum()) > maksAntallMøter) {
            if (antallMøterFør > antallMøterEtter) {
                let fjernetMåned = møterPerMånedTrunkert.shift()!;
                månederFjernetFraStart.push(fjernetMåned); //legger fjernet måned til i slutten av månederFjernetFraStart array - slik at mnd blir stigende
                antallMøterFør -= fjernetMåned.maxAntallMøterPerGruppe;
            } else {
                let fjernetMåned = møterPerMånedTrunkert.pop()!;
                månederFjernetFraSlutt.unshift(fjernetMåned); //legger fjernet måned til i start av månederFjernetFraSlutt array - slik at mnd blir stigende
                antallMøterEtter -= fjernetMåned.maxAntallMøterPerGruppe;
            }
            debug('fjernet måneder for å nå under terskelverdi', møterPerMånedTrunkert)
        }

        //dersom bruker har valgt å se alle møter i start eller slutt - legger vi disse tilbake her
        if (this.ikkeTrunkerStart) {
            møterPerMånedTrunkert.unshift(...månederFjernetFraStart)
            månederFjernetFraStart = [];
        }
        if (this.ikkeTrunkerSlutt) {
            møterPerMånedTrunkert.push(...månederFjernetFraSlutt)
            månederFjernetFraSlutt = [];
        }

        return [new Trunkeringsinfo(månederFjernetFraStart, månederFjernetFraSlutt, this.gjeldendeTidssone.brukersTidssone()), møterPerMånedTrunkert];
    }

    private tilgjengeligeHorisontalPlass() {
        debug('tilgjengeligeHorisontalPlass', this.kontainerbredde())
        let tilgjengeligBredde = this.kontainerbredde() - this.margin;
        if (this.trunkeringsinfo.trunkertSlutt.erTrunkert) tilgjengeligBredde -= this.trunkertKontainerBredde;
        if (this.trunkeringsinfo.trunkertStart.erTrunkert) tilgjengeligBredde -= this.trunkertKontainerBredde;

        const labelsBredde = Math.max(this.minLabelsKontainerBredde, tilgjengeligBredde * this.relativLabelKontainerBredde);
        const møterBredde = Math.max(this.minMøterKontainerBredde, tilgjengeligBredde - labelsBredde);

        return {labelsBredde: labelsBredde, møterBredde: møterBredde};
    }

    //årshjulrelatert

    private beregnÅrshjulinnslagForGruppe(innslagsamling: l.ÅrshjulInnslagModell[]): Årshjulinfo {
        const ukenummer = [...this.posisjonUker.keys()];
        const førsteUke = ukenummer.reduce(...reduserTilMin());
        const sisteUke = ukenummer.reduce(...reduserTilMaks());

        //fjern innslag som er utenfor uker-vinduet vårt
        let justertInnslagsamling = innslagsamling
          .map(innslag => this.årshjulInnslagIPlanModell(innslag))
          .filter(innslag => innslag.startuke <= sisteUke && (innslag.startuke + innslag.antallUker - 1) >= førsteUke);

        //juster varighet ihht ukevinduet vårt slik at innslagenes start/slutt ikke overskrider uker-vinduet vårt
        justertInnslagsamling.forEach(innslag => {
            if (innslag.startuke < førsteUke) {
                innslag.antallUker = innslag.antallUker - (førsteUke - innslag.startuke);
                innslag.startuke = førsteUke;
            }
            if ((innslag.startuke + innslag.antallUker) > sisteUke) {
                innslag.antallUker = (sisteUke - innslag.startuke) + 1;
            }
        });

        //brekk opp perioder som spenner over årsskifte
        [...justertInnslagsamling]
          .filter(innslag => (innslag.startuke + innslag.antallUker) > 53)
          .forEach(innslagSomSpennerOverÅrsskifte => {
              const hode: ÅrshjulInnslagIPlanModell = {...innslagSomSpennerOverÅrsskifte};
              innslagSomSpennerOverÅrsskifte.antallUker = 53 - innslagSomSpennerOverÅrsskifte.startuke;
              hode.startuke = 1;
              hode.antallUker = hode.antallUker - innslagSomSpennerOverÅrsskifte.antallUker;
              justertInnslagsamling.unshift(hode); //legger til nytt innslag som representerer perioden fra uke 1 og utover
          });

        //beregn posisjonering
        justertInnslagsamling.forEach(innslag => {
            const sluttuke = innslag.startuke + innslag.antallUker - 1;
            const startUkePos = this.posisjonUker.get(innslag.startuke)!;
            const sluttUkePos = this.posisjonUker.get(innslag.startuke + innslag.antallUker - 1)!;
            innslag.pos = {tl: startUkePos.tl, tr: sluttUkePos.tr, bredde: sluttUkePos.tr - startUkePos.tl};
            innslag.skalertPos = {...innslag.pos}; //justeres av oppdaterSkalertePosisjonerÅrshjullinnslag()
            innslag.sluttukeJustert = sluttuke > 52 ? sluttuke - 52 : sluttuke; //@todo ??
        });

        //brekk innslag over multiple arrayer der hver array ikke inneholder overlappende innslag
        const kollisjonsfriInnslagsamling = <ÅrshjulInnslagIPlanModell[][]>tilKolisjonsfrieÅrshjulinnslagsamlinger(justertInnslagsamling);
        debug("beregnÅrshjulinnslagForgruppe", kollisjonsfriInnslagsamling)
        return {
            antallRader: innslagsamling.length > 0 ? kollisjonsfriInnslagsamling.length : 0,
            rader: kollisjonsfriInnslagsamling
        };
    }

    private årshjulInnslagIPlanModell(innslag: l.ÅrshjulInnslagModell): ÅrshjulInnslagIPlanModell {
        return <ÅrshjulInnslagIPlanModell>({orginal: innslag, pos: {tl: 0, tr: 0, bredde: 0}, ...innslag}); //@todo hvorfor er ikke sluttukeJustert def?
    }

    //skaleringsrelatert

    private skalerPlanIhtVindusbredde() {
        const tilgjengeligPlass = this.tilgjengeligeHorisontalPlass();
        debug('skalerPlanIhhtVindusbredde, tilgjengeligPlass:', tilgjengeligPlass)

        this.oppdaterSkalertePosisjonerMøter(tilgjengeligPlass.møterBredde);
        this.oppdaterSkalertePosisjonerÅrshjullinnslag(tilgjengeligPlass.møterBredde);

        this.labelsKontainerBredde = tilgjengeligPlass.labelsBredde;
        this.moeterKontainerBredde = siste(this.moeterPerMaaned)!.skalertPos.tr;
        this.naaPos = this.beregnNåPosisjon();
    }

    private oppdaterSkalertePosisjonerMøter(møterKontainerBredde: number) {
        const preSkaleringMøterBredde = siste(this.moeterPerMaaned)!.pos.tr;
        const skalering = møterKontainerBredde / preSkaleringMøterBredde;
        debug('oppdaterSkalertePosisjonerMøter, møterKontainerBredde:%d, skalering:%s, post skalering bredde: %s', møterKontainerBredde, skalering, preSkaleringMøterBredde * skalering);

        this.moeterPerMaaned.forEach(mndInfo => {
            mndInfo.skalering = skalering;
            mndInfo.skalertPos = this.skalerPosisjon(mndInfo.pos, skalering);
            mndInfo.moeterIMnd.forEach(møteInfo => møteInfo.skalertPos = this.skalerPosisjon(møteInfo.pos, skalering))
        })
    }

    private oppdaterSkalertePosisjonerÅrshjullinnslag(møterKontainerBredde: number) {
        const preSkaleringÅrshjulBredde = [...this.posisjonUker.values()].map(uke => uke.tr).reduce(...reduserTilMaks());
        const skalering = møterKontainerBredde / preSkaleringÅrshjulBredde;
        debug('oppdaterSkalertePosisjonerÅrshjullinnslag, møterKontainerBredde:%d, skalering:%s, post skalering bredde: %s', møterKontainerBredde, skalering, preSkaleringÅrshjulBredde * skalering);

        this.aarshjulSomMap.forEach((årshjulInfo) => {
            årshjulInfo.rader.flat().forEach((innslag: ÅrshjulInnslagIPlanModell) =>
              innslag.skalertPos = this.skalerPosisjon(innslag.pos, skalering));
        });
        this.aarshjul = mapSomObjekt(this.aarshjulSomMap);
    }

    /** finn pos til "nå" i tidslinjen, som pixel posisjon left. */
    private beregnNåPosisjon(): number {
        const nå = this.klokke.nåSomZonedDateTime();
        return this.moeterPerMaaned
          .filter(mndInfo => erSammeÅrOgMånedZonedDateTime(nå, this.somZonedDateTime(mndInfo.mnd)))
          .map(mndInfo => {
              //ingen møter i måneden - posisjoner "nå" midt mellom månedens start og slutt
              if (erTomListe(mndInfo.moeterIMnd))
                  return mndInfo.skalertPos.tl + mndInfo.skalertPos.bredde / 2;

              //finn førstkommende møte
              let førstkommende = mndInfo.moeterIMnd
                .sort((a, b) => a.moete.dato.start.getTime() - b.moete.dato.start.getTime()) //må sortere på dato for at 'finn første' skal bli korrekt
                .find(møteInfo => erZonedDateTimeSenereEnn(this.somZonedDateTime(møteInfo.moete.dato.slutt), nå));
              //alternativt siste møte i måneden
              return førstkommende ?
                førstkommende.skalertPos.tl - this.møteHorisontalMargin :
                siste(mndInfo.moeterIMnd)!.skalertPos.tr + this.møteHorisontalMargin;
          })
          .reduce(...reduserTilFørsteInnslagEller(0));
    }

    //øvrige hjelpemetoder

    private somZonedDateTime(dato: Date): ZoneDateTime {
        return dateSomZonedDateTime(dato, this.gjeldendeTidssone.brukersTidssone())
    }

    private somPlainDate(dato: Date): PlainDate {
        return this.somZonedDateTime(dato).toPlainDate()
    }
}

export class Trunkeringsinfo {

    erTrunkert: boolean;
    trunkertStart: TrunkeringsinfoDetaljer;
    trunkertSlutt: TrunkeringsinfoDetaljer;

    static ingenTrunkering() {
        return new Trunkeringsinfo([], [], Tidssoneinformasjon.UTC); //ingen dato-operasjoner - kan derfor angi konstant tidssone
    }

    constructor(månederFjernetFraStart: MånedInfo[], månederFjernetFraSlutt: MånedInfo[], tidssone:Tidssoneinformasjon) {
        this.erTrunkert = månederFjernetFraStart.length > 0 || månederFjernetFraSlutt.length > 0;
        this.trunkertStart = this.somTrunkeringsInfoDetaljer(månederFjernetFraStart, tidssone);
        this.trunkertSlutt = this.somTrunkeringsInfoDetaljer(månederFjernetFraSlutt, tidssone);
    }

    private somTrunkeringsInfoDetaljer(måneder: MånedInfo[], tidssone:Tidssoneinformasjon): TrunkeringsinfoDetaljer {
        var antallMøterPerGruppe = this.reduserTilAntallMøterPerGruppe(måneder);
        return {
            erTrunkert: måneder.length > 0,
            periode: this.tilPeriodeSomSpennerOver(måneder, tidssone),
            antallMoeterPerGruppe: mapSomObjekt(antallMøterPerGruppe),
            antallMoeterPerGruppeSomMap: antallMøterPerGruppe
        }
    }

    private reduserTilAntallMøterPerGruppe(møterPerMåned: MånedInfo[]): Map<GruppeId, number> {
        return møterPerMåned
          .map(måned => måned.moeterPerGruppeSomMap)
          .map(møterPerGruppe => mapVerdierIMap(møterPerGruppe, møter => møter.length))
          .reduce((akkumulert, antallPerGruppe) => {
              antallPerGruppe.forEach((antall, gruppeId) => akkumulert.set(gruppeId, (akkumulert.get(gruppeId) ?? 0) + antall));
              return akkumulert
          }, new Map<GruppeId, number>());
    }

    private tilPeriodeSomSpennerOver(møterPerMåned: MånedInfo[], tidssone:Tidssoneinformasjon): DatoPeriode | null {
        if (møterPerMåned.length == 0) return null;
        var startMnd = dateSomZonedDateTime(første(møterPerMåned)!.mnd, tidssone);
        var sluttMnd = dateSomZonedDateTime(siste(møterPerMåned)!.mnd, tidssone);
        return {
            start: zonedDateTimeSomDate(zonedDateTimeFørsteDagIMåneden(startMnd)),
            slutt: zonedDateTimeSomDate(zonedDateTimeSisteDagIMåneden(sluttMnd))
        };
    }
}

type ÅrshjulinnslagPerGruppe = { [gruppeId: GruppeId]: l.ÅrshjulInnslagModell[] }

interface Årshjulinfo {
    antallRader: number;
    rader: ÅrshjulInnslagIPlanModell[][];
}

export interface TrunkeringsinfoDetaljer {
    erTrunkert: boolean;
    /** Perioden som er trunkert, kun satt dersom erTrunkert */
    periode: DatoPeriode | null;
    /** @deprecated  erstattes av antallMoeterPerGruppeSomMap*/
    antallMoeterPerGruppe: { [gruppeId: string]: number };
    antallMoeterPerGruppeSomMap: Map<GruppeId, number>;
}

export interface DatoPeriode {
    start: Date;
    slutt: Date;
}

export interface ÅrshjulInnslagIPlanModell extends l.ÅrshjulInnslagModell {
    sluttukeJustert: number;
    orginal: l.ÅrshjulInnslagModell;
    pos: TlTrPosisjon;
    skalertPos: TlTrPosisjon;
}

export interface MånedInfo {
    pos: TlTrPosisjon;
    skalertPos: TlTrPosisjon;
    skalering: number;
    mnd: Date;
    mndTemporal: PlainDate;
    moeterIMnd: MøteInfo[];
    /** @deprecated  erstattes av moeterPerGruppeSomMap*/
    moeterPerGruppe: { [gruppeId: string]: MøteInfo[] };
    moeterPerGruppeSomMap: Map<GruppeId, MøteInfo[]>;
    /** antall møter for den gruppen som har det høyeste antall møter i denne måneden*/
    maxAntallMøterPerGruppe: number;
}

export interface MøteInfo {
    moete: l.MøteModellEnkel;
    pos: TlTrPosisjon;
    skalertPos: TlTrPosisjon;
}

export interface TlTrPosisjon {
    tl: number;
    tr: number;
    bredde: number;
}
